import React, { memo, useCallback, useMemo } from 'react';

import {
  buildCitationDisplayProps,
  formatCitationLabel,
} from 'app/pages/loanApplication/components/GuidelineCitations/citationMarkers.utils';
import GuidelineCitationBadge from 'app/pages/loanApplication/components/GuidelineCitations/GuidelineCitationBadge/GuidelineCitationBadge';
import PropTypes from 'prop-types';

import { LoadStatus } from 'constants/aiProcessRuns';

import { resolveCitationDisplayState } from '../AiActivityCitations.utils';
import { useOpenCitationFullView } from '../hooks/useOpenCitationFullView';

// LEN-213: an inline guideline citation in AI Activity — the shared chat citation chip
// (GuidelineCitationBadge) that opens the shared guideline drawer. Unresolved markers
// render as a neutral chip with a plain section label, never as a raw [[CITE:…]] tag.
const CitationChip = ({ citation = null, citationKey, status = LoadStatus.READY }) => {
  const openCitationFullView = useOpenCitationFullView();
  const { isPending, isUnavailable } = resolveCitationDisplayState(citation, status);

  const displayProps = useMemo(
    () => buildCitationDisplayProps(citation, citationKey),
    [citation, citationKey],
  );
  const fallbackLabel = useMemo(
    () => formatCitationLabel(citation, citationKey),
    [citation, citationKey],
  );

  const onChipClick = useCallback(
    () => openCitationFullView(displayProps),
    [openCitationFullView, displayProps],
  );

  return (
    <GuidelineCitationBadge
      citation={displayProps}
      fallbackLabel={fallbackLabel}
      isPending={isPending}
      isUnavailable={isUnavailable}
      onClick={onChipClick}
    />
  );
};

CitationChip.propTypes = {
  citationKey: PropTypes.string.isRequired,
  citation: PropTypes.shape({
    citationId: PropTypes.string,
    citationNumber: PropTypes.number,
    sectionId: PropTypes.string,
    sourceId: PropTypes.string,
    sourceName: PropTypes.string,
    sourceShortName: PropTypes.string,
    sectionNumber: PropTypes.string,
    sectionTitle: PropTypes.string,
    excerpt: PropTypes.string,
  }),
  status: PropTypes.oneOf(Object.values(LoadStatus)),
};

export default memo(CitationChip);
